import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabase";
import {
  getOptimizedImageUrl,
  ImagePresets,
} from "@/lib/utils/supabaseImageTransform";
import { useAuthStore } from "@/store";
import useImageModelStore from "@/store/imageModelStore";
import { useThreadStore } from "@/store/threadStore";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Download,
  Edit,
  GripVertical,
  Info,
  Maximize,
  MessageSquare,
  Minimize,
  PanelRightClose,
  PanelRightOpen,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export const ImageLightboxPage = ({
  isModal = false,
  onClose,
  initialImageData,
  initialAllImages,
}) => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { fetchUserThreads } = useThreadStore();
  const {
    selectedEditModel,
    setSelectedEditModel,
    getEnabledModels,
    getSelectedEditModel,
  } = useImageModelStore();

  // Group enabled editing models by provider
  const groupedEditModels = useMemo(() => {
    const enabledModels = getEnabledModels("editing");
    return enabledModels.reduce((acc, model) => {
      if (!acc[model.provider]) {
        acc[model.provider] = [];
      }
      acc[model.provider].push(model);
      return acc;
    }, {});
  }, [getEnabledModels]);

  // Initialize state from props if available (for instant modal rendering)
  const [image, setImage] = useState(initialImageData || null);
  const [allImages, setAllImages] = useState(initialAllImages || []);
  // Skip loading state if we have initial data (instant modal rendering)
  const [isLoading, setIsLoading] = useState(!initialImageData);
  const [imageTitle, setImageTitle] = useState(initialImageData?.prompt || "");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editProgress, setEditProgress] = useState(0);
  const [editTimeElapsed, setEditTimeElapsed] = useState(0);
  const [editAbortController, setEditAbortController] = useState(null);
  const [editedVersions, setEditedVersions] = useState([]);
  // Track when thread images are being fetched (for History tab parent image lookup)
  const [threadImagesLoading, setThreadImagesLoading] = useState(
    !!initialImageData?.threadId
  );
  const [zoom, setZoom] = useState(1);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("image-lightbox-sidebar-width");
    return saved ? parseInt(saved) : 384;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [promptHeight, setPromptHeight] = useState(() => {
    const saved = localStorage.getItem("image-lightbox-prompt-height");
    return saved ? parseInt(saved) : 240;
  });
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const editTimerRef = useRef(null);
  const textareaRef = useRef(null);
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);
  const fullscreenContainerRef = useRef(null);
  const resizeStartXRef = useRef(null);
  const resizeStartWidthRef = useRef(null);
  const promptResizeStartYRef = useRef(null);
  const promptResizeStartHeightRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const controlsTimeoutRef = useRef(null);

  // Guard to prevent render loop on direct URL navigation
  // Once images are loaded, don't re-run the main effect even if callbacks are recreated
  const hasLoadedRef = useRef(false);

  // Track viewport center point for zoom-to-point functionality
  const viewportCenterRef = useRef(null);

  // Zoom handlers - preserve viewport center
  const handleZoomIn = useCallback(() => {
    if (zoom >= 4) return;

    // Capture current viewport center before zoom
    if (imageContainerRef.current && imageRef.current && zoom > 0) {
      const container = imageContainerRef.current;
      const img = imageRef.current;

      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        let viewportCenterX, viewportCenterY;

        if (zoom <= 1) {
          // At 100% zoom or less, image is centered via flexbox
          // Viewport center is the center of the container
          // In the wrapper coordinate system (5000px), this will be at 2500, 2500
          const wrapperSize = 5000;
          viewportCenterX = wrapperSize / 2;
          viewportCenterY = wrapperSize / 2;
        } else {
          // When zoomed in, use scroll position
          const scrollLeft = container.scrollLeft;
          const scrollTop = container.scrollTop;
          viewportCenterX = scrollLeft + containerWidth / 2;
          viewportCenterY = scrollTop + containerHeight / 2;
        }

        // Store for use in zoom effect
        viewportCenterRef.current = {
          x: viewportCenterX,
          y: viewportCenterY,
          zoom: zoom,
        };
      }
    }

    setZoom((prev) => Math.min(prev + 0.25, 4));
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    if (zoom <= 0.5) return;

    // Capture current viewport center before zoom
    if (imageContainerRef.current && imageRef.current && zoom > 0) {
      const container = imageContainerRef.current;
      const img = imageRef.current;

      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        let viewportCenterX, viewportCenterY;

        if (zoom <= 1) {
          // At 100% zoom or less, image is centered via flexbox
          // Viewport center is the center of the container
          // In the wrapper coordinate system (5000px), this will be at 2500, 2500
          const wrapperSize = 5000;
          viewportCenterX = wrapperSize / 2;
          viewportCenterY = wrapperSize / 2;
        } else {
          // When zoomed in, use scroll position
          const scrollLeft = container.scrollLeft;
          const scrollTop = container.scrollTop;
          viewportCenterX = scrollLeft + containerWidth / 2;
          viewportCenterY = scrollTop + containerHeight / 2;
        }

        // Store for use in zoom effect
        viewportCenterRef.current = {
          x: viewportCenterX,
          y: viewportCenterY,
          zoom: zoom,
        };
      }
    }

    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  }, [zoom]);

  const handleResetZoom = useCallback(() => {
    viewportCenterRef.current = null; // Reset to center of image
    setZoom(1);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      fullscreenContainerRef.current?.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
        toast.error("Failed to enter fullscreen mode");
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Track browser fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);

      // Clear sessionStorage flag when user manually exits fullscreen
      // BUT NOT during navigation (we want to preserve it)
      if (!document.fullscreenElement && !isNavigatingRef.current) {
        sessionStorage.removeItem("imageLightboxFullscreen");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Restore fullscreen state after navigation
  useEffect(() => {
    const shouldBeFullscreen =
      sessionStorage.getItem("imageLightboxFullscreen") === "true";

    if (
      shouldBeFullscreen &&
      !document.fullscreenElement &&
      fullscreenContainerRef.current
    ) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        fullscreenContainerRef.current
          ?.requestFullscreen()
          .then(() => {
            // Successfully restored fullscreen, clear navigation flag
            isNavigatingRef.current = false;
            sessionStorage.removeItem("imageLightboxFullscreen");
          })
          .catch((err) => {
            console.error("Error restoring fullscreen:", err);
            isNavigatingRef.current = false;
            sessionStorage.removeItem("imageLightboxFullscreen");
          });
      }, 100);

      return () => clearTimeout(timer);
    } else {
      // No fullscreen to restore, clear navigation flag
      isNavigatingRef.current = false;
    }
  }, [imageId]); // Re-run when imageId changes (navigation)

  // Auto-hide controls in fullscreen after inactivity
  useEffect(() => {
    if (!isFullscreen) {
      setShowControls(true);
      return;
    }

    const resetTimer = () => {
      setShowControls(true);

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000); // Hide after 3 seconds of inactivity
    };

    // Show controls initially
    resetTimer();

    // Event listeners for mouse and touch activity (but not all keyboard keys)
    const handleActivity = () => {
      resetTimer();
    };

    const handleKeyDown = (e) => {
      // Don't show controls for arrow keys (used for navigation)
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        return;
      }
      // Show controls for other keys
      resetTimer();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("mousedown", handleActivity);
    window.addEventListener("touchstart", handleActivity);
    window.addEventListener("touchmove", handleActivity);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("mousedown", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("touchmove", handleActivity);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  // Save sidebar width to localStorage
  useEffect(() => {
    localStorage.setItem(
      "image-lightbox-sidebar-width",
      sidebarWidth.toString()
    );
  }, [sidebarWidth]);

  // Save prompt height to localStorage
  useEffect(() => {
    localStorage.setItem(
      "image-lightbox-prompt-height",
      promptHeight.toString()
    );
  }, [promptHeight]);

  // Auto-focus textarea when Edit tab is active
  useEffect(() => {
    if (activeTab === "edit" && textareaRef.current && !isEditing) {
      textareaRef.current.focus();
    }
  }, [activeTab, isEditing]);

  // Resize handlers
  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = sidebarWidth;
    },
    [sidebarWidth]
  );

  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizing) return;
      const delta = resizeStartXRef.current - e.clientX;
      const newWidth = Math.max(
        320,
        Math.min(600, resizeStartWidthRef.current + delta)
      );
      setSidebarWidth(newWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeEnd);
      return () => {
        window.removeEventListener("mousemove", handleResizeMove);
        window.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Action handlers (defined early for keyboard shortcuts)
  const handleClose = useCallback(() => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate("/library");
    }
  }, [isModal, onClose, navigate]);

  const handleOpenInChat = useCallback(() => {
    if (!image?.threadId) return;
    // Use hash fragment with imageId parameter
    const url = `/chat/${image.threadId}#imageId=${imageId}`;
    navigate(url);
  }, [image, imageId, navigate]);

  const handleDownload = useCallback(async () => {
    if (!image) return;

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = `${image.prompt.substring(0, 30).replace(/[^a-zA-Z0-9]/g, "_")}_${image.id}.png`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Image downloaded");
    } catch (error) {
      console.error("Error downloading image:", error);
      toast.error("Failed to download image");
    }
  }, [image]);

  // Prompt resize handlers
  const handlePromptResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizingPrompt(true);
      promptResizeStartYRef.current = e.clientY;
      promptResizeStartHeightRef.current = promptHeight;
    },
    [promptHeight]
  );

  const handlePromptResizeMove = useCallback(
    (e) => {
      if (!isResizingPrompt) return;
      const delta = e.clientY - promptResizeStartYRef.current;
      const newHeight = Math.max(
        100,
        Math.min(600, promptResizeStartHeightRef.current + delta)
      );
      setPromptHeight(newHeight);
    },
    [isResizingPrompt]
  );

  const handlePromptResizeEnd = useCallback(() => {
    setIsResizingPrompt(false);
  }, []);

  useEffect(() => {
    if (isResizingPrompt) {
      window.addEventListener("mousemove", handlePromptResizeMove);
      window.addEventListener("mouseup", handlePromptResizeEnd);
      return () => {
        window.removeEventListener("mousemove", handlePromptResizeMove);
        window.removeEventListener("mouseup", handlePromptResizeEnd);
      };
    }
  }, [isResizingPrompt, handlePromptResizeMove, handlePromptResizeEnd]);

  const handleCopyPrompt = useCallback(async () => {
    if (!image?.prompt) return;
    try {
      await navigator.clipboard.writeText(image.prompt);
      setPromptCopied(true);
      toast.success("Prompt copied to clipboard");
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (error) {
      console.error("Error copying prompt:", error);
      toast.error("Failed to copy prompt");
    }
  }, [image]);

  // Generate short UUID-like ID from long ID (deterministic hash)
  const generateShortId = useCallback((longId) => {
    if (!longId) return "";
    // Simple hash function to create 8-character identifier
    let hash = 0;
    for (let i = 0; i < longId.length; i++) {
      const char = longId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Convert to base36 and pad to 8 characters (UUID-like format)
    return Math.abs(hash).toString(36).padStart(8, "0").substring(0, 8);
  }, []);

  // Detect image dimensions and update size metadata if missing
  const detectAndSaveImageSize = useCallback(async (imageData) => {
    if (!imageData || !imageData.url) return;

    // Check if size is missing or N/A
    const needsSize = !imageData.imageSize || imageData.imageSize === "N/A";
    if (!needsSize) return;

    // Helper to load image and get dimensions
    const loadImage = (url, useCors = false) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        if (useCors) {
          img.crossOrigin = "anonymous";
        }
        img.onload = () => {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          } else {
            reject(new Error("Invalid dimensions"));
          }
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = url;
      });
    };

    try {
      let dimensions;

      // Try loading without CORS first (works for most cases)
      try {
        dimensions = await loadImage(imageData.url, false);
      } catch {
        // If that fails, try with CORS (needed for some external images)
        try {
          dimensions = await loadImage(imageData.url, true);
        } catch {
          // Both failed - show "Unknown" instead of staying on "Detecting..."
          console.warn("Could not detect image dimensions for:", imageData.url);
          setImage((prev) => ({
            ...prev,
            imageSize: "Unknown",
          }));
          return;
        }
      }

      const sizeString = `${dimensions.width} x ${dimensions.height}`;

      console.log(`Detected image size: ${sizeString}`);

      // Update local state first (works for all image types)
      setImage((prev) => ({
        ...prev,
        imageSize: sizeString,
      }));

      // For user uploads, skip database save (no tool_calls to update)
      if (imageData.isUserUpload) {
        console.log("Image size detected for user upload:", sizeString);
        return;
      }

      // For AI-generated images, save to database if messageId exists
      if (!imageData.messageId) return;

      // Update the message's tool_calls to include the size
      const { data: message, error: fetchError } = await supabase
        .from("thread_messages")
        .select("tool_calls")
        .eq("message_id", imageData.messageId)
        .single();

      if (fetchError) throw fetchError;

      let toolCalls = message.tool_calls;
      if (typeof toolCalls === "string") {
        toolCalls = JSON.parse(toolCalls);
      }

      // Safety check: ensure toolCalls is an array
      if (!Array.isArray(toolCalls)) {
        console.warn(
          "tool_calls is not an array, skipping size save to database"
        );
        return;
      }

      // Find and update the relevant tool call
      const updatedToolCalls = toolCalls.map((tc) => {
        const isRelevant =
          imageData.id.includes(tc.id) ||
          ["image_gen", "image_edit", "generate_images"].includes(
            tc.function?.name
          );

        if (isRelevant) {
          return {
            ...tc,
            result: {
              ...tc.result,
              data: {
                ...tc.result?.data,
                parameters: {
                  ...tc.result?.data?.parameters,
                  size: sizeString,
                },
              },
              metadata: {
                ...tc.result?.metadata,
                parameters: {
                  ...tc.result?.metadata?.parameters,
                  size: sizeString,
                },
                size_detected_at: new Date().toISOString(),
              },
            },
          };
        }
        return tc;
      });

      // Save back to database
      const { error: updateError } = await supabase
        .from("thread_messages")
        .update({ tool_calls: updatedToolCalls })
        .eq("message_id", imageData.messageId);

      if (updateError) throw updateError;

      console.log("Image size saved to metadata:", sizeString);
    } catch (error) {
      console.error("Error detecting or saving image size:", error);
    }
  }, []);

  // Detect size for initial image data when component mounts
  useEffect(() => {
    if (initialImageData && !initialImageData.imageSize) {
      detectAndSaveImageSize(initialImageData);
    }
  }, [initialImageData, detectAndSaveImageSize]);

  // Fetch all images from a specific thread (both AI-generated and user-uploaded)
  const fetchThreadImages = useCallback(async (threadId) => {
    if (!threadId) return [];

    try {
      // Fetch all messages from this thread
      const { data: messages, error } = await supabase
        .from("thread_messages")
        .select(
          "id, message_id, role, content, metadata, tool_calls, created_at"
        )
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const allThreadImages = [];

      for (const msg of messages || []) {
        // 1. Extract AI-generated images from tool_calls
        if (msg.tool_calls) {
          const toolCalls =
            typeof msg.tool_calls === "string"
              ? JSON.parse(msg.tool_calls)
              : msg.tool_calls;

          for (const tc of toolCalls || []) {
            if (
              ["image_gen", "image_edit", "generate_images"].includes(
                tc.function?.name
              )
            ) {
              const imageUrl = tc.result?.data?.url || tc.result?.url;
              if (imageUrl) {
                const toolName = tc.function?.name;
                const isEditedImage =
                  toolName === "image_edit" || toolName?.includes("image_edit");

                // Extract parent image URL for edited images (for History tab)
                let parentImageUrl = null;
                let originalImageUrl = null;
                if (isEditedImage) {
                  originalImageUrl =
                    tc.result?.metadata?.original_image_url || null;
                  try {
                    const functionArgs = tc.function?.arguments;
                    const args =
                      typeof functionArgs === "string"
                        ? JSON.parse(functionArgs)
                        : functionArgs;
                    parentImageUrl =
                      args?.imageUrls?.[0] || args?.image || null;
                  } catch {
                    // Ignore parse errors
                  }
                }

                allThreadImages.push({
                  id: `${msg.message_id}-${tc.id}`,
                  url: imageUrl,
                  thumbnailUrl: imageUrl,
                  prompt:
                    tc.result?.data?.prompt ||
                    tc.function?.arguments?.prompt ||
                    (isEditedImage ? "Edited Image" : "Generated Image"),
                  messageId: msg.message_id,
                  threadId: threadId,
                  createdAt: msg.created_at,
                  imageProvider:
                    tc.result?.metadata?.provider || tc.result?.data?.provider,
                  imageModel:
                    tc.result?.metadata?.model || tc.result?.data?.model,
                  imageSize:
                    tc.result?.metadata?.parameters?.size ||
                    tc.result?.data?.parameters?.size,
                  toolName,
                  isEditedImage,
                  isUserUpload: false,
                  metadata: {
                    ...tc,
                    parentImageUrl,
                    original_image_url: originalImageUrl || parentImageUrl,
                  },
                });
              }
            }
          }
        }

        // 2. Extract user-uploaded images from storage_files metadata
        if (msg.role === "user" && msg.metadata?.storage_files) {
          const files =
            typeof msg.metadata.storage_files === "string"
              ? JSON.parse(msg.metadata.storage_files)
              : msg.metadata.storage_files;

          for (const file of files || []) {
            const isImage =
              file.isImage ||
              file.type?.startsWith("image/") ||
              /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(
                file.name || file.path || ""
              );

            if (isImage) {
              // Build the full URL for user uploads
              let imageUrl = file.publicUrl || file.url;
              if (!imageUrl && file.path) {
                // Get public URL from Supabase storage
                const { data: publicUrlData } = supabase.storage
                  .from("chat")
                  .getPublicUrl(file.path);
                imageUrl = publicUrlData?.publicUrl;
              }

              if (imageUrl) {
                allThreadImages.push({
                  id: file.id || `${msg.message_id}-${file.path || file.name}`,
                  url: imageUrl,
                  thumbnailUrl: imageUrl,
                  prompt: file.name || "User Upload",
                  name: file.name,
                  messageId: msg.message_id,
                  threadId: threadId,
                  createdAt: msg.created_at,
                  imageProvider: "User Upload",
                  imageModel: null,
                  toolName: "storage",
                  isUserUpload: true,
                });
              }
            }
          }
        }
      }

      // Sort by creation date (chronological order)
      return allThreadImages.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    } catch (error) {
      console.error("Error fetching thread images:", error);
      return [];
    }
  }, []);

  // Fetch thread images when opened from a thread context
  useEffect(() => {
    // DEBUG: Log thread images fetch condition
    console.log("[Lightbox Navigation] Thread images fetch check:", {
      hasThreadId: !!initialImageData?.threadId,
      threadId: initialImageData?.threadId,
      allImagesLength: allImages.length,
      shouldFetch: initialImageData?.threadId && allImages.length <= 1,
    });

    if (initialImageData?.threadId && allImages.length <= 1) {
      fetchThreadImages(initialImageData.threadId).then((threadImages) => {
        // DEBUG: Log fetched thread images
        console.log("[Lightbox Navigation] Thread images fetched:", {
          count: threadImages.length,
          imageIds: threadImages.map((img) => img.id),
          currentImageId: initialImageData?.id,
          currentImageInFetched: threadImages.some(
            (img) => img.id === initialImageData?.id
          ),
        });

        if (threadImages.length > 0) {
          setAllImages(threadImages);

          // Update current image with enriched data from thread images
          // (so it has toolName, metadata, etc. for History tab)
          setImage((currentImage) => {
            if (!currentImage) return currentImage;
            const enrichedImage = threadImages.find(
              (img) =>
                img.id === currentImage.id || img.url === currentImage.url
            );
            if (enrichedImage) {
              console.log(
                "[Lightbox Navigation] Enriching current image with thread data:",
                {
                  currentId: currentImage.id,
                  enrichedToolName: enrichedImage.toolName,
                }
              );
              return { ...currentImage, ...enrichedImage };
            }
            return currentImage;
          });
        }

        // Mark thread images loading as complete
        setThreadImagesLoading(false);
      });
    } else if (initialImageData?.threadId) {
      // Already have thread images loaded, mark as not loading
      setThreadImagesLoading(false);
    } else {
      // Not opened from thread context, no thread images to load
      setThreadImagesLoading(false);
    }
  }, [
    initialImageData?.threadId,
    initialImageData?.id,
    fetchThreadImages,
    allImages.length,
  ]);

  // Navigate to previous image in the sequence
  const handlePreviousImage = useCallback(() => {
    if (!image || allImages.length === 0) return;

    const currentIndex = allImages.findIndex((img) => img.id === image.id);
    if (currentIndex > 0) {
      const prevImage = allImages[currentIndex - 1];
      const shortId = generateShortId(prevImage.id);

      // Update image state directly
      setImage(prevImage);
      // Detect size for new image if needed
      detectAndSaveImageSize(prevImage);

      // Update URL without navigation (keeps component mounted, preserves fullscreen)
      window.history.replaceState(null, "", `/library/image/${shortId}`);
    }
  }, [image, allImages, generateShortId, detectAndSaveImageSize]);

  // Navigate to next image in the sequence
  const handleNextImage = useCallback(() => {
    if (!image || allImages.length === 0) return;

    const currentIndex = allImages.findIndex((img) => img.id === image.id);
    if (currentIndex < allImages.length - 1) {
      const nextImage = allImages[currentIndex + 1];
      const shortId = generateShortId(nextImage.id);

      // Update image state directly
      setImage(nextImage);
      // Detect size for new image if needed
      detectAndSaveImageSize(nextImage);

      // Update URL without navigation (keeps component mounted, preserves fullscreen)
      window.history.replaceState(null, "", `/library/image/${shortId}`);
    }
  }, [image, allImages, generateShortId, detectAndSaveImageSize]);

  // Keyboard shortcuts (defined AFTER handlers)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in textarea
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT")
        return;

      switch (e.key.toLowerCase()) {
        case "arrowleft":
          e.preventDefault();
          handlePreviousImage();
          break;
        case "arrowright":
          e.preventDefault();
          handleNextImage();
          break;
        case "e":
          e.preventDefault();
          setActiveTab("edit");
          break;
        case "d":
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            handleDownload();
          }
          break;
        case "c":
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            if (image?.threadId) handleOpenInChat();
          }
          break;
        case "i":
          e.preventDefault();
          setActiveTab("details");
          break;
        case "h":
          e.preventDefault();
          setActiveTab("history");
          break;
        case "escape":
          e.preventDefault();
          // Only close if in modal mode (has onClose callback)
          if (isModal && onClose) {
            handleClose();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    image,
    handleDownload,
    handleOpenInChat,
    handleClose,
    handlePreviousImage,
    handleNextImage,
    isModal,
    onClose,
  ]);

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        // Capture current viewport center before zoom
        if (imageContainerRef.current && imageRef.current && zoom > 0) {
          const container = imageContainerRef.current;
          const img = imageRef.current;

          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            let viewportCenterX, viewportCenterY;

            if (zoom <= 1) {
              // At 100% zoom or less, image is centered via flexbox
              // Viewport center is the center of the container
              // In the wrapper coordinate system (5000px), this will be at 2500, 2500
              const wrapperSize = 5000;
              viewportCenterX = wrapperSize / 2;
              viewportCenterY = wrapperSize / 2;
            } else {
              // When zoomed in, use scroll position
              const scrollLeft = container.scrollLeft;
              const scrollTop = container.scrollTop;
              viewportCenterX = scrollLeft + containerWidth / 2;
              viewportCenterY = scrollTop + containerHeight / 2;
            }

            // Store for use in zoom effect
            viewportCenterRef.current = {
              x: viewportCenterX,
              y: viewportCenterY,
              zoom: zoom,
            };
          }
        }

        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom((prev) => Math.max(0.5, Math.min(4, prev + delta)));
      }
    },
    [zoom]
  );

  // Constrain scroll to image edges when zoomed
  const handleScroll = useCallback(() => {
    if (zoom <= 1 || !imageContainerRef.current || !imageRef.current) return;

    const container = imageContainerRef.current;
    const img = imageRef.current;

    // Get image natural dimensions
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    if (naturalWidth === 0 || naturalHeight === 0) return;

    // Calculate scaled dimensions
    const scaledWidth = naturalWidth * zoom;
    const scaledHeight = naturalHeight * zoom;

    // Get container dimensions
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Image is centered in 5000px wrapper
    const wrapperSize = 5000;
    const imageCenterX = wrapperSize / 2;
    const imageCenterY = wrapperSize / 2;

    // Calculate image bounds in wrapper coordinates
    const imageLeft = imageCenterX - scaledWidth / 2;
    const imageRight = imageCenterX + scaledWidth / 2;
    const imageTop = imageCenterY - scaledHeight / 2;
    const imageBottom = imageCenterY + scaledHeight / 2;

    // Calculate min/max scroll positions
    // We want the container viewport to not go beyond the image edges
    const minScrollLeft = Math.max(0, imageLeft);
    const maxScrollLeft = Math.min(
      wrapperSize - containerWidth,
      imageRight - containerWidth
    );
    const minScrollTop = Math.max(0, imageTop);
    const maxScrollTop = Math.min(
      wrapperSize - containerHeight,
      imageBottom - containerHeight
    );

    // Clamp scroll position immediately (no delay to prevent streaking)
    let newScrollLeft = container.scrollLeft;
    let newScrollTop = container.scrollTop;
    let needsUpdate = false;

    if (newScrollLeft < minScrollLeft) {
      newScrollLeft = minScrollLeft;
      needsUpdate = true;
    } else if (newScrollLeft > maxScrollLeft) {
      newScrollLeft = maxScrollLeft;
      needsUpdate = true;
    }

    if (newScrollTop < minScrollTop) {
      newScrollTop = minScrollTop;
      needsUpdate = true;
    } else if (newScrollTop > maxScrollTop) {
      newScrollTop = maxScrollTop;
      needsUpdate = true;
    }

    // Apply clamped scroll position immediately if it changed
    if (needsUpdate) {
      container.scrollLeft = newScrollLeft;
      container.scrollTop = newScrollTop;
    }
  }, [zoom]);

  // Center scroll position when zoomed in to allow scrolling to all edges
  // Preserves viewport center point when zooming
  useEffect(() => {
    // Clear viewport center when zoom resets to 1 or below
    if (zoom <= 1) {
      viewportCenterRef.current = null;
      return;
    }

    if (zoom > 1 && imageContainerRef.current && imageRef.current) {
      const container = imageContainerRef.current;
      const img = imageRef.current;

      // Wait for image to load if needed
      if (img.complete && img.naturalWidth > 0) {
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        const scaledWidth = naturalWidth * zoom;
        const scaledHeight = naturalHeight * zoom;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        const wrapperSize = 5000;
        const imageCenterX = wrapperSize / 2;
        const imageCenterY = wrapperSize / 2;

        // Calculate image bounds in wrapper coordinates
        const imageLeft = imageCenterX - scaledWidth / 2;
        const imageRight = imageCenterX + scaledWidth / 2;
        const imageTop = imageCenterY - scaledHeight / 2;
        const imageBottom = imageCenterY + scaledHeight / 2;

        const minScrollLeft = Math.max(0, imageLeft);
        const maxScrollLeft = Math.min(
          wrapperSize - containerWidth,
          imageRight - containerWidth
        );
        const minScrollTop = Math.max(0, imageTop);
        const maxScrollTop = Math.min(
          wrapperSize - containerHeight,
          imageBottom - containerHeight
        );

        let targetScrollLeft, targetScrollTop;

        // If we have a saved viewport center, preserve it
        if (viewportCenterRef.current) {
          const savedCenter = viewportCenterRef.current;
          const oldZoom = savedCenter.zoom;

          // Convert the old viewport center point to image coordinates
          // The viewport center in old zoom coordinates
          const oldViewportCenterX = savedCenter.x;
          const oldViewportCenterY = savedCenter.y;

          // Calculate the image point that was at the viewport center
          // In the old zoom, the image was centered at wrapperSize/2
          const oldImageCenterX = wrapperSize / 2;
          const oldImageCenterY = wrapperSize / 2;
          const oldScaledWidth = naturalWidth * oldZoom;
          const oldScaledHeight = naturalHeight * oldZoom;
          const oldImageLeft = oldImageCenterX - oldScaledWidth / 2;
          const oldImageTop = oldImageCenterY - oldScaledHeight / 2;

          // Image point in image coordinates (0 to naturalWidth/Height)
          const imagePointX =
            ((oldViewportCenterX - oldImageLeft) / oldScaledWidth) *
            naturalWidth;
          const imagePointY =
            ((oldViewportCenterY - oldImageTop) / oldScaledHeight) *
            naturalHeight;

          // Now calculate where this point should be in the new zoom
          const newImageLeft = imageCenterX - scaledWidth / 2;
          const newImageTop = imageCenterY - scaledHeight / 2;
          const newViewportCenterX =
            newImageLeft + (imagePointX / naturalWidth) * scaledWidth;
          const newViewportCenterY =
            newImageTop + (imagePointY / naturalHeight) * scaledHeight;

          // Set scroll position to center this point in the viewport
          targetScrollLeft = newViewportCenterX - containerWidth / 2;
          targetScrollTop = newViewportCenterY - containerHeight / 2;

          // Clear the saved center after using it
          viewportCenterRef.current = null;
        } else {
          // No saved center, center on the image
          targetScrollLeft = imageCenterX - containerWidth / 2;
          targetScrollTop = imageCenterY - containerHeight / 2;
        }

        // Clamp to bounds
        const centerScrollLeft = Math.max(
          minScrollLeft,
          Math.min(maxScrollLeft, targetScrollLeft)
        );
        const centerScrollTop = Math.max(
          minScrollTop,
          Math.min(maxScrollTop, targetScrollTop)
        );

        container.scrollLeft = centerScrollLeft;
        container.scrollTop = centerScrollTop;
      } else {
        // Fallback: center in wrapper
        container.scrollLeft = 2500 - container.clientWidth / 2;
        container.scrollTop = 2500 - container.clientHeight / 2;
      }
    }
  }, [zoom, image?.url]);

  // Decode imageId from URL (supports both short UUID and legacy base64)
  const decodedImageId = useCallback(
    (images = []) => {
      if (!imageId) return null;

      // Check if it's a short UUID format (8 characters, alphanumeric)
      if (imageId.length === 8 && /^[a-z0-9]+$/.test(imageId)) {
        // Find image by matching short hash
        for (const img of images) {
          const hash = generateShortId(img.id);
          if (hash === imageId) {
            return img.id;
          }
        }
        return null;
      }

      // Fallback to old base64 format for backward compatibility
      try {
        return atob(decodeURIComponent(imageId));
      } catch (e) {
        console.error("Error decoding imageId:", e);
        return null;
      }
    },
    [imageId, generateShortId]
  );

  // Background fetch for allImages (used when modal has initial single image but needs navigation)
  const fetchAllImagesInBackground = useCallback(async () => {
    if (!user?.id) return;

    try {
      await fetchUserThreads(user.id, {
        preventAutomaticCreation: true,
        setCurrentThread: false,
      });

      const { threads: fetchedThreads } = useThreadStore.getState();
      const userThreadIds = fetchedThreads.map((thread) => thread.id);

      if (userThreadIds.length === 0) return;

      const { data, error } = await supabase.rpc("get_user_images_by_threads", {
        thread_ids: userThreadIds,
      });

      if (error || !data) return;

      // Map images with full data (including toolName, isEditedImage, metadata for History tab)
      const mappedImages = data.map((item) => {
        const toolCallData =
          typeof item.tool_call_data === "string"
            ? (() => {
                try {
                  return JSON.parse(item.tool_call_data);
                } catch {
                  return null;
                }
              })()
            : item.tool_call_data || null;

        const toolName =
          item.tool_name ||
          toolCallData?.function?.name ||
          toolCallData?.name ||
          "image_gen";
        const isEditedImage =
          toolName === "image_edit" || toolName?.includes("image_edit");

        const id = item.tool_call_id
          ? `${item.message_id}-${item.tool_call_id}`
          : item.message_id;

        const prompt =
          item.image_prompt ||
          item.image_title ||
          item.image_name ||
          (isEditedImage ? "Edited Image" : "Generated Image");

        // Extract parent image URL for edited images
        let parentImageUrl = null;
        let originalImageUrl = null;
        if (isEditedImage && toolCallData) {
          originalImageUrl =
            toolCallData.result?.metadata?.original_image_url || null;
          try {
            const functionArgs = toolCallData.function?.arguments;
            const args =
              typeof functionArgs === "string"
                ? JSON.parse(functionArgs)
                : functionArgs;
            parentImageUrl = args?.imageUrls?.[0] || args?.image || null;
          } catch {
            // Ignore parse errors
          }
        }

        const threadTitle =
          fetchedThreads.find((thread) => thread.id === item.thread_id)
            ?.title || "Untitled Chat";

        return {
          id,
          url: item.image_url,
          thumbnailUrl: getOptimizedImageUrl(
            item.image_url,
            ImagePresets.preview
          ),
          prompt,
          toolName,
          createdAt: item.created_at || new Date().toISOString(),
          threadId: item.thread_id,
          threadTitle,
          messageId: item.message_id,
          metadata: {
            ...(toolCallData || {}),
            parentImageUrl,
            original_image_url: originalImageUrl || parentImageUrl,
          },
          imageSize: item.image_size,
          imageModel: item.image_model,
          imageProvider: item.image_provider,
          isDirectUrl: item.is_direct_url,
          storagePath: item.storage_path,
          storageBucket: item.storage_bucket,
          isEditedImage,
        };
      });

      // Deduplicate and sort
      const uniqueImages = Array.from(
        new Map(mappedImages.map((img) => [img.id, img])).values()
      );
      uniqueImages.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setAllImages(uniqueImages);

      // Update current image with enriched data if it matches
      setImage((currentImage) => {
        if (!currentImage) return currentImage;
        const enrichedImage = uniqueImages.find(
          (img) => img.id === currentImage.id || img.url === currentImage.url
        );
        if (enrichedImage) {
          // Merge enriched data with current image (preserve any local state)
          return { ...currentImage, ...enrichedImage };
        }
        return currentImage;
      });
    } catch (error) {
      console.error("Error loading images in background:", error);
    }
  }, [user?.id, fetchUserThreads]);

  // Fetch all images and find the current one
  useEffect(() => {
    const loadImages = async () => {
      // Guard: Don't re-run if already loaded (prevents render loop on direct URL navigation)
      // This stops the effect from re-running when callbacks are recreated
      if (hasLoadedRef.current && allImages.length > 0 && image) {
        return;
      }

      if (!user?.id) return;

      // If we have initial data from navigation state, use it immediately
      // and skip loading for instant modal rendering
      if (initialImageData && !isLoading) {
        // If opened from a thread context, let fetchThreadImages handle navigation
        // (don't overwrite thread images with all library images)
        // Only fetch all library images if opened from library (no threadId)
        if (
          !initialImageData?.threadId &&
          (!initialAllImages || initialAllImages.length === 0)
        ) {
          // Background fetch for navigation - don't block rendering
          fetchAllImagesInBackground();
        }
        return;
      }

      setIsLoading(true);
      try {
        // Fetch all threads
        await fetchUserThreads(user.id, {
          preventAutomaticCreation: true,
          setCurrentThread: false,
        });

        const { threads: fetchedThreads } = useThreadStore.getState();
        const userThreadIds = fetchedThreads.map((thread) => thread.id);

        if (userThreadIds.length === 0) {
          setAllImages([]);
          setIsLoading(false);
          return;
        }

        // Use the same RPC function as LibraryPage to ensure consistent IDs
        const { data, error } = await supabase.rpc(
          "get_user_images_by_threads",
          { thread_ids: userThreadIds }
        );

        if (error) {
          console.error("Error loading images via RPC:", error);
          setAllImages([]);
          setIsLoading(false);
          return;
        }

        // Map images using the same logic as LibraryPage
        const mappedImages =
          (data || []).map((item) => {
            const toolCallData =
              typeof item.tool_call_data === "string"
                ? (() => {
                    try {
                      return JSON.parse(item.tool_call_data);
                    } catch {
                      return null;
                    }
                  })()
                : item.tool_call_data || null;

            const toolName =
              item.tool_name ||
              toolCallData?.function?.name ||
              toolCallData?.name ||
              "image_gen";
            const isEditedImage =
              toolName === "image_edit" || toolName?.includes("image_edit");

            const prompt =
              item.image_prompt ||
              item.image_title ||
              item.image_name ||
              (isEditedImage ? "Edited Image" : "Generated Image");

            // Use the same ID generation as LibraryPage
            const id = item.tool_call_id
              ? `${item.message_id}-${item.tool_call_id}`
              : item.message_id;

            const thumbnailUrl = getOptimizedImageUrl(
              item.image_url,
              ImagePresets.preview
            );

            const threadTitle =
              fetchedThreads.find((thread) => thread.id === item.thread_id)
                ?.title || "Untitled Chat";

            // Extract parent image URL from tool call arguments or result metadata for edited images
            let parentImageUrl = null;
            let originalImageUrl = null;
            if (isEditedImage && toolCallData) {
              // First, try to get from result metadata (new format from imageEditTool.js)
              originalImageUrl =
                toolCallData.result?.metadata?.original_image_url || null;

              // Fallback: extract from tool call arguments
              try {
                const functionArgs = toolCallData.function?.arguments;
                const args =
                  typeof functionArgs === "string"
                    ? JSON.parse(functionArgs)
                    : functionArgs;

                // Get the first image URL from the imageUrls array
                parentImageUrl = args?.imageUrls?.[0] || args?.image || null;
              } catch (e) {
                console.warn(
                  "Failed to parse tool call arguments for parent image:",
                  e
                );
              }
            }

            // Fallback: extract size/model/provider from metadata if not in RPC data
            let imageSize = item.image_size;
            let imageModel = item.image_model;
            let imageProvider = item.image_provider;

            // For older images, try extracting from tool_call_data metadata/parameters
            if (!imageSize || !imageModel || !imageProvider) {
              if (toolCallData?.result?.metadata) {
                imageSize =
                  imageSize || toolCallData.result.metadata.parameters?.size;
                imageModel = imageModel || toolCallData.result.metadata.model;
                imageProvider =
                  imageProvider || toolCallData.result.metadata.provider;
              }
              if (toolCallData?.result?.data?.parameters) {
                imageSize =
                  imageSize || toolCallData.result.data.parameters.size;
                imageModel = imageModel || toolCallData.result.data.model;
                imageProvider =
                  imageProvider || toolCallData.result.data.provider;
              }

              // Try to infer provider from URL patterns for FAL images
              if (!imageProvider && item.image_url) {
                if (
                  item.image_url.includes("fal.media") ||
                  item.image_url.includes("fal.ai")
                ) {
                  imageProvider = "fal";
                  // Try to infer model from URL or default to recraft
                  if (!imageModel) {
                    imageModel = "recraft-v3";
                  }
                } else if (
                  item.image_url.includes(
                    "oaidalleapiprodscus.blob.core.windows.net"
                  )
                ) {
                  imageProvider = "openai";
                } else if (item.image_url.includes("storage.googleapis.com")) {
                  imageProvider = "google";
                }
              }
            }

            return {
              id,
              url: item.image_url,
              thumbnailUrl,
              prompt,
              toolName,
              createdAt: item.created_at || new Date().toISOString(),
              threadId: item.thread_id,
              threadTitle,
              messageId: item.message_id,
              metadata: {
                ...(toolCallData || {}),
                parentImageUrl, // Add parent image URL for easy lookup (from arguments)
                original_image_url: originalImageUrl || parentImageUrl, // Add for History tab lookup (prefer result metadata, fallback to arguments)
              },
              imageSize,
              imageModel,
              imageProvider,
              isDirectUrl: item.is_direct_url,
              storagePath: item.storage_path,
              storageBucket: item.storage_bucket,
              isEditedImage,
            };
          }) || [];

        // Deduplicate by id (in case RPC returns duplicates)
        const uniqueImages = Array.from(
          new Map(mappedImages.map((img) => [img.id, img])).values()
        );

        // Merge with existing allImages to preserve manually added images
        // (e.g., newly edited images that haven't appeared in RPC yet)
        const existingImagesMap = new Map(
          allImages.map((img) => [img.id, img])
        );

        // Add any existing images that aren't in RPC results (preserve manually added)
        uniqueImages.forEach((img) => {
          existingImagesMap.set(img.id, img); // RPC results take precedence
        });

        // Keep existing images that aren't in RPC (but only if they're recent - within last 5 minutes)
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        allImages.forEach((img) => {
          if (!existingImagesMap.has(img.id)) {
            const imgDate = new Date(img.createdAt).getTime();
            // Only preserve if recent (likely a newly added image)
            if (imgDate > fiveMinutesAgo) {
              existingImagesMap.set(img.id, img);
            }
          }
        });

        // Sort images by date
        const sortedImages = Array.from(existingImagesMap.values()).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setAllImages(sortedImages);

        // Mark as loaded to prevent render loop from callback recreation
        hasLoadedRef.current = true;

        // Find current image
        const decoded = decodedImageId(sortedImages);
        if (decoded) {
          const currentImage = sortedImages.find((img) => img.id === decoded);
          if (currentImage) {
            setImage(currentImage);
            // Detect and save size if missing
            detectAndSaveImageSize(currentImage);
          } else {
            // Fallback 1: Try to find image by matching the short ID hash of any image
            // This handles cases where ID generation differs slightly (e.g., toolCall.id vs 'default')
            let foundImage = null;
            for (const img of sortedImages) {
              const imgShortId = generateShortId(img.id);
              if (imgShortId === imageId) {
                foundImage = img;
                break;
              }
            }

            if (foundImage) {
              setImage(foundImage);
              // Detect and save size if missing
              detectAndSaveImageSize(foundImage);
            } else {
              console.warn(
                "Image not found by ID hash, available images:",
                sortedImages.length
              );
              // Note: We can't easily do URL-based fallback here since we only have the short ID
              // The short ID is a hash of the full ID, not the URL
              // The best we can do is show an error and redirect
              toast.error("Image not found");
              navigate("/library");
            }
          }
        } else {
          // If decoding failed, try to find by short ID hash directly
          let foundImage = null;
          for (const img of sortedImages) {
            const imgShortId = generateShortId(img.id);
            if (imgShortId === imageId) {
              foundImage = img;
              break;
            }
          }

          if (foundImage) {
            setImage(foundImage);
            // Detect and save size if missing
            detectAndSaveImageSize(foundImage);
          } else {
            console.warn(
              "Image not found by short ID, available images:",
              sortedImages.length
            );
            toast.error("Image not found");
            navigate("/library");
          }
        }
      } catch (error) {
        console.error("Error loading images:", error);
        toast.error("Failed to load image");
      } finally {
        setIsLoading(false);
      }
    };

    loadImages();
  }, [
    user?.id,
    imageId,
    fetchUserThreads,
    navigate,
    decodedImageId,
    generateShortId,
    detectAndSaveImageSize,
    initialImageData,
    initialAllImages,
    isLoading,
    fetchAllImagesInBackground,
    allImages,
    image,
  ]);

  // Reset loading guard when imageId changes in standalone mode
  // This allows re-fetching when navigating to a different image via URL
  useEffect(() => {
    // Only reset if in standalone mode (no initialImageData from modal navigation)
    if (!initialImageData) {
      hasLoadedRef.current = false;
    }
  }, [imageId, initialImageData]);

  // Edit timer
  useEffect(() => {
    if (isEditing) {
      editTimerRef.current = setInterval(() => {
        setEditTimeElapsed((prev) => prev + 1);
        setEditProgress((prev) => Math.min(prev + 3.33, 95)); // Reach 95% in ~30 seconds
      }, 1000);
    } else {
      if (editTimerRef.current) {
        clearInterval(editTimerRef.current);
        editTimerRef.current = null;
      }
      setEditTimeElapsed(0);
      setEditProgress(0);
    }

    return () => {
      if (editTimerRef.current) {
        clearInterval(editTimerRef.current);
      }
    };
  }, [isEditing]);

  const handleEditImage = async () => {
    if (!editPrompt.trim() || !image) {
      toast.error("Please enter an edit prompt");
      return;
    }

    // Create abort controller for this edit operation
    const abortController = new AbortController();
    setEditAbortController(abortController);
    setIsEditing(true);
    setEditProgress(0);
    setEditTimeElapsed(0);

    try {
      // Create user message with edit request and image thumbnail FIRST
      // This shows the user's intent before the AI processes it
      if (image.threadId) {
        const { addOptimisticMessage, saveMessageToDatabase } =
          useThreadStore.getState();
        const userMessageId = crypto.randomUUID();
        const userMessage = {
          id: userMessageId,
          thread_id: image.threadId,
          role: "user",
          content: editPrompt,
          created_at: new Date().toISOString(),
          metadata: {
            isImageEditRequest: true,
            imageEditContext: {
              originalImageUrl: image.url,
              editPrompt: editPrompt,
            },
          },
        };

        // Add user message to UI immediately
        addOptimisticMessage(image.threadId, userMessage);

        // Save user message to database
        try {
          await saveMessageToDatabase(userMessage);
        } catch (dbError) {
          console.error(
            "[ImageLightbox] Failed to save user edit message:",
            dbError
          );
          // Don't throw - continue with edit even if save fails
        }
      }

      // Get selected editing model from store
      const editModel = getSelectedEditModel();

      // Validate model supports editing
      if (!editModel?.capabilities?.editing) {
        toast.error("Selected model does not support editing");
        setIsEditing(false);
        return;
      }

      // Import ImageEditTool dynamically
      const { ImageEditTool } = await import("@/lib/ai/tools/imageEditTool");
      const editTool = new ImageEditTool();

      const result = await editTool.execute({
        prompt: editPrompt,
        imageUrls: [image.url],
        model: editModel.id,
        provider: editModel.provider,
        signal: abortController.signal,
      });

      // Handle the response structure from ImageEditTool
      // Result structure: { success: true, result: { data: { images: [...] } } }
      if (result.success && result.result?.data?.images?.length > 0) {
        const editedImage = result.result.data.images[0];
        setEditProgress(100);

        // Add edited image as a new message in the thread if threadId exists
        if (image.threadId) {
          const { addOptimisticMessage, saveMessageToDatabase } =
            useThreadStore.getState();
          const toolCallId = crypto.randomUUID();

          // Combine original prompt with edit prompt for full context
          const combinedPrompt = image.prompt
            ? `${image.prompt}\n\n${editPrompt}`
            : editPrompt;

          const newMessage = {
            id: crypto.randomUUID(),
            thread_id: image.threadId,
            role: "assistant",
            content: `Edited image based on: "${editPrompt}"`,
            tool_calls: [
              {
                id: toolCallId,
                function: {
                  name: "image_edit",
                  arguments: JSON.stringify({
                    prompt: editPrompt,
                    imageUrls: [image.url],
                  }),
                },
                result: {
                  data: {
                    url: editedImage.url,
                    prompt: combinedPrompt,
                    original_prompt: image.prompt,
                    edit_prompt: editPrompt,
                    // Add fields that RPC function expects
                    model: result.result?.data?.model || editModel.id,
                    provider:
                      result.result?.data?.provider || editModel.provider,
                    size:
                      result.result?.data?.parameters?.size ||
                      image.imageSize ||
                      "1024x1024",
                  },
                  metadata: {
                    original_image_id: image.id,
                    original_image_url: image.url,
                    edit_timestamp: Date.now(),
                    storage_path: editedImage.storagePath,
                  },
                },
              },
            ],
            created_at: new Date().toISOString(),
          };

          // Add to UI immediately
          addOptimisticMessage(image.threadId, newMessage);

          // Save to database
          try {
            await saveMessageToDatabase(newMessage);
          } catch (dbError) {
            console.error(
              "[ImageLightbox] Failed to save edited image message to database:",
              dbError
            );
            // Don't throw - the message is already in the UI
          }

          // Generate the stable ID using the same format as RPC (simple, not hash-based)
          // RPC format: `${message_id}-${tool_call_id}` or just `message_id`
          const stableId = `${newMessage.id}-${toolCallId}`;

          // Add the new edit to editedVersions immediately
          setEditedVersions((prev) => [
            ...prev,
            {
              id: newMessage.id,
              imageId: stableId,
              messageId: newMessage.id,
              threadId: image.threadId,
              url: editedImage.url,
              prompt: editPrompt,
              createdAt: newMessage.created_at,
              storagePath: editedImage.storagePath,
            },
          ]);

          // Create the new edited image object directly
          const newEditedImageObj = {
            id: stableId,
            messageId: newMessage.id,
            threadId: image.threadId,
            url: editedImage.url,
            thumbnailUrl: getOptimizedImageUrl(
              editedImage.url,
              ImagePresets.preview
            ),
            prompt: editPrompt,
            toolName: "image_edit",
            createdAt: newMessage.created_at,
            threadTitle: image.threadTitle || "Untitled Chat",
            storagePath: editedImage.storagePath,
            parentImageUrl: image.url,
            imageProvider: editModel.provider,
            imageModel: editModel.id,
            isEditedImage: true,
            metadata: {
              parentImageUrl: image.url,
              original_image_url: image.url,
              original_image_id: image.id,
            },
          };

          // Add to allImages for navigation (at the beginning for easy access)
          setAllImages((prev) => {
            // Check if image already exists to avoid duplicates
            const exists = prev.some((img) => img.id === stableId);
            if (exists) {
              return prev.map((img) =>
                img.id === stableId ? newEditedImageObj : img
              );
            }
            return [newEditedImageObj, ...prev];
          });

          // Update current image directly (instant display - no navigation flash)
          setImage(newEditedImageObj);
          setImageTitle(editPrompt);

          // Update URL without full navigation (keeps component mounted)
          const shortId = generateShortId(stableId);
          window.history.replaceState(null, "", `/library/image/${shortId}`);

          // Detect size for new image
          detectAndSaveImageSize(newEditedImageObj);

          toast.success("Image edited successfully");
        } else {
          // No threadId - create a temporary ID and update directly
          const tempStableId = `temp-${Date.now()}-${crypto.randomUUID()}`;

          // Create the temporary image object
          const tempImage = {
            id: tempStableId,
            url: editedImage.url,
            thumbnailUrl: getOptimizedImageUrl(
              editedImage.url,
              ImagePresets.preview
            ),
            prompt: editPrompt,
            toolName: "image_edit",
            createdAt: new Date().toISOString(),
            parentImageUrl: image.url,
            imageProvider: editModel.provider,
            imageModel: editModel.id,
            isEditedImage: true,
            metadata: {
              parentImageUrl: image.url,
              original_image_url: image.url,
              original_image_id: image.id,
            },
          };

          // Add to allImages
          setAllImages((prev) => [tempImage, ...prev]);

          // Update current image directly (instant display)
          setImage(tempImage);
          setImageTitle(editPrompt);

          // Update URL without navigation
          const shortId = generateShortId(tempStableId);
          window.history.replaceState(null, "", `/library/image/${shortId}`);

          // Detect size for new image
          detectAndSaveImageSize(tempImage);

          toast.success("Image edited successfully");
        }
      } else {
        console.error("[ImageLightbox] Invalid edit result:", result);
        throw new Error(
          result.error || "Image edit failed - invalid response structure"
        );
      }
    } catch (error) {
      console.error("Error editing image:", error);
      if (error.name === "AbortError") {
        toast.info("Image edit cancelled");
      } else {
        toast.error("Failed to edit image");
      }
    } finally {
      setIsEditing(false);
      setEditPrompt("");
      setEditAbortController(null);
    }
  };

  // Generate a concise title from the prompt
  const generateTitleFromPrompt = useCallback((prompt) => {
    if (!prompt) return "Generated Image";

    // Simple title generation: take first sentence or up to 60 chars
    let title = prompt.split(/[.!?]/)[0].trim();
    if (title.length > 60) {
      title = title.substring(0, 60).trim();
      // Cut at last space to avoid breaking words
      const lastSpace = title.lastIndexOf(" ");
      if (lastSpace > 40) {
        title = title.substring(0, lastSpace);
      }
      title += "...";
    }
    return title || "Generated Image";
  }, []);

  // Handle manual title generation button click using AI
  const handleGenerateTitle = useCallback(async () => {
    if (!image?.prompt) {
      toast.error("No prompt available to generate title");
      return;
    }

    setIsGeneratingTitle(true);

    try {
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3002";

      // Call the same backend /title endpoint used for thread titles
      const response = await fetch(`${API_BASE_URL}/api/ai/chat/title`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userMessage: { content: image.prompt },
          tracking: {
            userId: user?.id,
            threadId: image.threadId,
            featureId: "image_title_generation",
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Title generation failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.title) {
        throw new Error("Invalid title response from backend");
      }

      // Clean up the AI-generated title
      const cleanTitle = data.title
        .trim()
        .replace(/["'`]/g, "")
        .replace(/\.+$/, "")
        .trim();

      setImageTitle(cleanTitle);
      toast.success("Title generated with AI");
    } catch (error) {
      console.error("Error generating title with AI:", error);
      toast.error("Failed to generate title");
      // Fallback to simple generation
      const fallbackTitle = generateTitleFromPrompt(image.prompt);
      setImageTitle(fallbackTitle);
    } finally {
      setIsGeneratingTitle(false);
    }
  }, [image?.prompt, image?.threadId, user?.id, generateTitleFromPrompt]);

  // Save title to database
  const handleSaveTitle = useCallback(async () => {
    if (!image || !imageTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    setIsSavingTitle(true);
    try {
      // Update the message's tool_calls to include the title in metadata
      const { data: message, error: fetchError } = await supabase
        .from("thread_messages")
        .select("tool_calls")
        .eq("message_id", image.messageId)
        .single();

      if (fetchError) throw fetchError;

      let toolCalls = message.tool_calls;
      if (typeof toolCalls === "string") {
        toolCalls = JSON.parse(toolCalls);
      }

      // Find and update the relevant tool call
      const updatedToolCalls = toolCalls.map((tc) => {
        // Match by tool call ID or if it's the image generation/edit call
        const isRelevant =
          image.id.includes(tc.id) ||
          ["image_gen", "image_edit", "generate_images"].includes(
            tc.function?.name
          );

        if (isRelevant) {
          return {
            ...tc,
            result: {
              ...tc.result,
              data: {
                ...tc.result?.data,
                title: imageTitle,
              },
              metadata: {
                ...tc.result?.metadata,
                title: imageTitle,
                title_updated_at: new Date().toISOString(),
              },
            },
          };
        }
        return tc;
      });

      // Save back to database
      const { error: updateError } = await supabase
        .from("thread_messages")
        .update({ tool_calls: updatedToolCalls })
        .eq("message_id", image.messageId);

      if (updateError) throw updateError;

      // Update local state
      setImage((prev) => ({
        ...prev,
        title: imageTitle,
        metadata: {
          ...prev.metadata,
          title: imageTitle,
        },
      }));

      setIsEditingTitle(false);
      toast.success("Title saved successfully");
    } catch (error) {
      console.error("Error saving title:", error);
      toast.error("Failed to save title");
    } finally {
      setIsSavingTitle(false);
    }
  }, [image, imageTitle]);

  const handleCancelEdit = () => {
    if (editAbortController) {
      editAbortController.abort();
      setEditAbortController(null);
      setIsEditing(false);
      setEditPrompt("");
      toast.info("Cancelling image edit...");
    }
  };

  const handleEditInChat = () => {
    if (!image?.threadId) {
      toast.error("This image is not associated with a conversation");
      return;
    }

    // Navigate to chat and let the edit continue in background
    navigate(`/chat/${image.threadId}#${image.messageId || ""}`);
  };

  // Emit image data to MainLayout header and initialize title
  useEffect(() => {
    if (image) {
      // Check for existing title in metadata
      const existingTitle =
        image.title ||
        image.metadata?.title ||
        image.metadata?.result?.data?.title ||
        image.metadata?.result?.metadata?.title;

      let titleToUse;
      if (existingTitle) {
        titleToUse = existingTitle;
      } else {
        // Generate title from prompt and save it
        titleToUse = generateTitleFromPrompt(image.prompt);
        // Auto-save generated title
        setImageTitle(titleToUse);
        // Skip auto-save for user-uploaded images (they don't have tool_calls)
        if (image.isUserUpload) {
          // User uploads don't have tool_calls to update, skip database save
        } else {
          // We'll save it in background for AI-generated images
          setTimeout(async () => {
            try {
              const { data: message, error: fetchError } = await supabase
                .from("thread_messages")
                .select("tool_calls")
                .eq("message_id", image.messageId)
                .single();

              if (fetchError) throw fetchError;

              let toolCalls = message.tool_calls;
              if (typeof toolCalls === "string") {
                toolCalls = JSON.parse(toolCalls);
              }

              // Safety check: ensure toolCalls is an array before mapping
              if (!Array.isArray(toolCalls)) {
                console.warn("tool_calls is not an array, skipping auto-save");
                return;
              }

              const updatedToolCalls = toolCalls.map((tc) => {
                const isRelevant =
                  image.id.includes(tc.id) ||
                  ["image_gen", "image_edit", "generate_images"].includes(
                    tc.function?.name
                  );

                if (isRelevant && !tc.result?.data?.title) {
                  return {
                    ...tc,
                    result: {
                      ...tc.result,
                      data: {
                        ...tc.result?.data,
                        title: titleToUse,
                      },
                      metadata: {
                        ...tc.result?.metadata,
                        title: titleToUse,
                        title_generated_at: new Date().toISOString(),
                      },
                    },
                  };
                }
                return tc;
              });

              await supabase
                .from("thread_messages")
                .update({ tool_calls: updatedToolCalls })
                .eq("message_id", image.messageId);
            } catch (error) {
              console.error("Error auto-saving generated title:", error);
            }
          }, 0);
        }
      }

      setImageTitle(titleToUse);

      const event = new CustomEvent("imageLightboxData", {
        detail: {
          prompt: titleToUse,
        },
      });
      window.dispatchEvent(event);
    }
  }, [image, generateTitleFromPrompt]);

  // Fetch edited versions of this image
  useEffect(() => {
    const fetchEditedVersions = async () => {
      if (!image?.threadId) {
        setEditedVersions([]);
        return;
      }

      try {
        // Query messages in the same thread where this image was edited
        const { data: messages, error } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("thread_id", image.threadId)
          .eq("role", "assistant")
          .not("tool_calls", "is", null)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error fetching edited versions:", error);
          return;
        }

        const edits = [];
        messages?.forEach((message) => {
          let toolCalls = message.tool_calls || [];
          if (typeof toolCalls === "string") {
            try {
              toolCalls = JSON.parse(toolCalls);
            } catch {
              return;
            }
          }

          toolCalls.forEach((toolCall) => {
            if (
              toolCall.function?.name === "image_edit" &&
              toolCall.result?.data?.url
            ) {
              // Check if this edit is related to our image
              const metadata = toolCall.result?.metadata || {};
              const toolArguments = toolCall.function?.arguments
                ? typeof toolCall.function.arguments === "string"
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments
                : null;

              if (
                metadata.original_image_id === image.id ||
                metadata.original_image_url === image.url ||
                toolArguments?.imageUrls?.[0] === image.url
              ) {
                // Generate the stable ID using the same format as RPC (simple, not hash-based)
                // RPC format: `${message_id}-${tool_call_id}` or just `message_id`
                const imageId = toolCall.id
                  ? `${message.message_id}-${toolCall.id}`
                  : message.message_id;

                edits.push({
                  id: message.message_id,
                  imageId: imageId, // Use the simple RPC format ID
                  messageId: message.message_id,
                  threadId: message.thread_id,
                  url: toolCall.result.data.url,
                  prompt: toolCall.result.data.prompt || toolArguments?.prompt,
                  createdAt: message.created_at,
                  storagePath: metadata.storage_path,
                });
              }
            }
          });
        });

        setEditedVersions(edits);
      } catch (error) {
        console.error("Error processing edited versions:", error);
      }
    };

    fetchEditedVersions();
  }, [image, allImages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading image...</p>
        </div>
      </div>
    );
  }

  if (!image) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <p className="text-xl text-muted-foreground mb-4">Image not found</p>
          <Button onClick={handleClose}>Back to Library</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Global cursor style when resizing */}
      {isResizing && (
        <style>{`
          * { cursor: col-resize !important; }
          body { user-select: none !important; }
        `}</style>
      )}
      {isResizingPrompt && (
        <style>{`
          * { cursor: row-resize !important; }
          body { user-select: none !important; }
        `}</style>
      )}

      {/* Main content area fills full height */}
      <div
        ref={fullscreenContainerRef}
        className="flex flex-col lg:flex-row flex-1 min-h-0 h-full bg-black overflow-y-auto lg:overflow-hidden"
        style={{ cursor: isFullscreen && !showControls ? "none" : "default" }}
      >
        {/* Left side: Image Viewer */}
        <div className="flex-1 relative bg-black overflow-hidden min-w-0 min-h-[50vh] lg:min-h-0 flex items-center justify-center">
          {/* Navigation arrows */}
          {/* DEBUG: Log navigation state */}
          {console.log("[Lightbox Navigation] Button render check:", {
            allImagesLength: allImages.length,
            showButtons: allImages.length > 1,
            currentImageId: image?.id,
            currentIndex: allImages.findIndex((img) => img.id === image?.id),
            allImageIds: allImages.slice(0, 5).map((img) => img.id),
          })}
          {allImages.length > 1 && (
            <>
              {/* Previous Image Button */}
              {(() => {
                const currentIndex = allImages.findIndex(
                  (img) => img.id === image?.id
                );
                return (
                  currentIndex > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handlePreviousImage}
                      className={`absolute left-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-sm border border-white/10 transition-all ${isFullscreen && !showControls ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                      title="Previous image (←)"
                    >
                      <ChevronLeft className="h-8 w-8" />
                    </Button>
                  )
                );
              })()}

              {/* Next Image Button */}
              {(() => {
                const currentIndex = allImages.findIndex(
                  (img) => img.id === image?.id
                );
                return (
                  currentIndex < allImages.length - 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleNextImage}
                      className={`absolute right-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-sm border border-white/10 transition-all ${isFullscreen && !showControls ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                      title="Next image (→)"
                    >
                      <ChevronRight className="h-8 w-8" />
                    </Button>
                  )
                );
              })()}
            </>
          )}

          {/* Close button - only show in modal mode */}
          {isModal && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className={`absolute left-4 top-4 z-30 h-10 w-10 rounded-lg bg-black/60 hover:bg-black/80 text-white backdrop-blur-sm border border-white/10 transition-all ${isFullscreen && !showControls ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              title="Close (Esc)"
            >
              <X className="h-5 w-5" />
            </Button>
          )}

          {/* Zoom controls - positioned outside scrollable container to stay fixed */}
          <div
            className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 bg-black/80 backdrop-blur-sm rounded-full px-3 py-2 z-40 shadow-lg border border-white/10 transition-opacity duration-300 ${isFullscreen && !showControls ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleZoomOut();
              }}
              className="text-white hover:bg-white/10"
              disabled={zoom <= 0.5}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <div className="flex items-center px-2 text-white text-sm font-medium min-w-[3rem] justify-center">
              {Math.round(zoom * 100)}%
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResetZoom();
              }}
              className="text-white hover:bg-white/10"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleZoomIn();
              }}
              className="text-white hover:bg-white/10"
              disabled={zoom >= 4}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <div className="w-px h-6 bg-white/20" />
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSidebar();
              }}
              className="text-white hover:bg-white/10 hidden lg:flex"
              title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
            >
              {isSidebarCollapsed ? (
                <PanelRightOpen className="h-4 w-4" />
              ) : (
                <PanelRightClose className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFullscreen();
              }}
              className="text-white hover:bg-white/10"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4" />
              ) : (
                <Maximize className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Image container - scrollable */}
          <div
            ref={imageContainerRef}
            className={`absolute inset-0 overflow-auto ${
              zoom <= 1 ? "flex items-center justify-center" : ""
            }`}
            onWheel={handleWheel}
            onScroll={zoom > 1 ? handleScroll : undefined}
            style={{
              cursor: zoom > 1 ? "grab" : "default",
              overscrollBehavior: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {zoom > 1 ? (
              <div
                style={{
                  width: "5000px",
                  height: "5000px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  transform: "translateZ(0)",
                }}
              >
                <img
                  ref={imageRef}
                  src={getOptimizedImageUrl(image.url, ImagePresets.fullsize)}
                  alt={image.prompt}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "center center",
                    willChange: "transform",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                  loading="eager"
                  decoding="async"
                />
              </div>
            ) : (
              <img
                ref={imageRef}
                src={getOptimizedImageUrl(image.url, ImagePresets.fullsize)}
                alt={image.prompt}
                className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "center center",
                }}
                loading="eager"
                decoding="async"
              />
            )}
          </div>
        </div>

        {/* Right side: Tabbed Sidebar with Resize Handle */}
        {!isSidebarCollapsed && (
          <>
            {/* Resize Handle - hidden on mobile */}
            <div
              className="hidden lg:block w-1 bg-border hover:bg-purple-600 cursor-col-resize transition-colors relative group lg:self-center lg:h-[85vh] lg:rounded-l"
              onMouseDown={handleResizeStart}
              style={{ cursor: isResizing ? "col-resize" : "default" }}
            >
              <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            {/* Sidebar */}
            <div
              className="bg-background border-t lg:border-t-0 lg:border-l border-border flex flex-col w-full lg:w-[var(--sidebar-width)] h-auto lg:h-full lg:max-h-[85vh] lg:min-w-[320px] lg:max-w-[600px] lg:self-center lg:rounded-l-lg lg:shadow-2xl"
              style={{
                "--sidebar-width": `${sidebarWidth}px`,
              }}
            >
              {/* Tabbed Content */}
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="flex-1 flex flex-col min-h-0"
              >
                <TabsList className="w-full rounded-none border-b border-border bg-transparent justify-start px-4">
                  <TabsTrigger
                    value="details"
                    className="data-[state=active]:bg-muted"
                  >
                    <Info className="h-4 w-4 mr-2" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="edit"
                    className="data-[state=active]:bg-muted"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </TabsTrigger>
                  <TabsTrigger
                    value="history"
                    className="data-[state=active]:bg-muted"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    History
                    {editedVersions.length > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-600 text-white rounded-full">
                        {editedVersions.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent
                  value="details"
                  className="flex-1 m-0 overflow-hidden"
                >
                  <ScrollArea className="h-full">
                    <div className="p-6 space-y-6">
                      {/* Title Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-muted-foreground">
                            Title
                          </h3>
                          {!isEditingTitle && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setIsEditingTitle(true)}
                              className="h-7 px-2"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                        {isEditingTitle ? (
                          <div className="space-y-2">
                            <Textarea
                              value={imageTitle}
                              onChange={(e) => setImageTitle(e.target.value)}
                              placeholder="Enter image title..."
                              className="min-h-[60px] resize-none"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveTitle}
                                disabled={isSavingTitle || !imageTitle.trim()}
                                className="flex-1"
                              >
                                {isSavingTitle ? "Saving..." : "Save"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleGenerateTitle}
                                disabled={isSavingTitle || isGeneratingTitle}
                                className="flex-1"
                              >
                                {isGeneratingTitle
                                  ? "Generating..."
                                  : "Generate"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setIsEditingTitle(false);
                                  setImageTitle(
                                    image.title ||
                                      generateTitleFromPrompt(image.prompt)
                                  );
                                }}
                                disabled={isSavingTitle}
                                className="flex-1"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-md border border-border bg-muted/30 p-3">
                            <p className="text-sm leading-relaxed break-words">
                              {imageTitle || "No title"}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Prompt Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-muted-foreground">
                            Prompt
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopyPrompt}
                            className="h-7 px-2"
                          >
                            {promptCopied ? (
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                        <div className="relative">
                          <div
                            className="overflow-y-auto rounded-md border border-border bg-muted/30 p-3"
                            style={{ height: `${promptHeight}px` }}
                          >
                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                              {image.prompt}
                            </p>
                          </div>
                          <div
                            className="h-1 bg-border hover:bg-purple-600 cursor-row-resize transition-colors relative group mt-1 rounded-full"
                            onMouseDown={handlePromptResizeStart}
                            style={{
                              cursor: isResizingPrompt
                                ? "row-resize"
                                : "default",
                            }}
                          >
                            <div className="absolute inset-x-0 -top-1 -bottom-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <GripVertical className="h-4 w-4 text-muted-foreground rotate-90" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Metadata Grid */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground mb-1">Provider</p>
                          <p className="font-medium text-xs capitalize">
                            {image.imageProvider || "Unknown"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Model</p>
                          <p className="font-medium text-xs">
                            {image.isUserUpload
                              ? "N/A"
                              : image.imageModel || "Unknown"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Size</p>
                          <p className="font-medium text-xs">
                            {image.imageSize || "Detecting..."}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Source</p>
                          <p className="font-medium text-xs">
                            {image.isUserUpload
                              ? "User Upload"
                              : image.toolName === "storage"
                                ? "Storage"
                                : "Generated"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Created</p>
                          <p className="font-medium text-xs">
                            {image.createdAt &&
                            !isNaN(new Date(image.createdAt).getTime())
                              ? new Date(image.createdAt).toLocaleString(
                                  undefined,
                                  {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  }
                                )
                              : "Unknown"}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2 pt-2 border-t border-border">
                        {image.threadId && (
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={handleOpenInChat}
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Open in Thread
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          onClick={handleDownload}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Image
                        </Button>
                      </div>

                      {/* Keyboard Shortcuts */}
                      <div className="space-y-2 pt-4 border-t border-border">
                        <h3 className="text-sm font-medium text-muted-foreground">
                          Keyboard Shortcuts
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Previous
                            </span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              ←
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Next</span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              →
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Edit</span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              E
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Details
                            </span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              I
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              History
                            </span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              H
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Open Chat
                            </span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              C
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Download
                            </span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              ⌘D
                            </kbd>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Close</span>
                            <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                              ESC
                            </kbd>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Edit Tab */}
                <TabsContent
                  value="edit"
                  className="flex-1 m-0 overflow-hidden"
                >
                  <ScrollArea className="h-full">
                    <div className="p-6 space-y-4">
                      {/* Model Selector */}
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          Editing Model
                        </label>
                        <Select
                          value={selectedEditModel || undefined}
                          onValueChange={(value) => {
                            console.log("Model selected:", value);
                            setSelectedEditModel(value);
                          }}
                          disabled={isEditing}
                        >
                          <SelectTrigger className="w-full bg-[#99CEFF]/10 border-[#0288D1]/20 hover:bg-[#99CEFF]/20 transition-colors">
                            <SelectValue placeholder="Select editing model" />
                          </SelectTrigger>
                          <SelectContent
                            className="z-[9999]"
                            position="popper"
                            sideOffset={5}
                            align="start"
                          >
                            {Object.keys(groupedEditModels).length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">
                                No editing models available. Enable models in
                                Settings.
                              </div>
                            ) : (
                              Object.entries(groupedEditModels).map(
                                ([provider, models]) => (
                                  <SelectGroup key={provider}>
                                    <SelectLabel className="text-xs uppercase">
                                      {provider}
                                    </SelectLabel>
                                    {models.map((model) => (
                                      <SelectItem
                                        key={model.id}
                                        value={model.id}
                                      >
                                        {model.name}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <Textarea
                        ref={textareaRef}
                        placeholder="Describe what you want to add, remove, or change..."
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        className="min-h-[150px]"
                        disabled={isEditing}
                      />

                      {isEditing && (
                        <div className="space-y-2">
                          <Progress value={editProgress} className="w-full" />
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              Editing image... {editTimeElapsed}s elapsed
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              className="h-7 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          onClick={handleEditImage}
                          disabled={
                            !editPrompt.trim() ||
                            isEditing ||
                            !selectedEditModel
                          }
                          className="flex-1"
                        >
                          {isEditing ? "Editing..." : "Edit Image"}
                        </Button>
                        {isEditing && image.threadId && (
                          <Button
                            variant="outline"
                            onClick={handleEditInChat}
                            className="flex-1"
                          >
                            Edit in Chat
                          </Button>
                        )}
                      </div>

                      {!selectedEditModel && (
                        <p className="text-xs text-amber-600 dark:text-amber-500">
                          Please select an editing model to continue
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* History Tab */}
                <TabsContent
                  value="history"
                  className="flex-1 m-0 overflow-hidden"
                >
                  <ScrollArea className="h-full">
                    <div className="p-6 space-y-6">
                      {/* Parent Image Section */}
                      {(() => {
                        // DEBUG: Log image properties BEFORE isEditedImage check
                        console.log("[History Tab] Image properties:", {
                          imageId: image?.id,
                          isEditedImage: image?.isEditedImage,
                          toolName: image?.toolName,
                          hasMetadata: !!image?.metadata,
                          metadataKeys: image?.metadata
                            ? Object.keys(image.metadata)
                            : [],
                          threadImagesLoading,
                        });

                        const isEditedImage =
                          image?.isEditedImage ||
                          image?.toolName === "image_edit" ||
                          image?.toolName?.includes("image_edit");

                        console.log(
                          "[History Tab] isEditedImage check result:",
                          isEditedImage
                        );

                        if (!isEditedImage) return null;

                        // If thread images are still loading, show a loading state
                        // (user-uploaded parent images won't be in allImages until thread fetch completes)
                        if (threadImagesLoading) {
                          return (
                            <div className="space-y-2">
                              <h3 className="text-sm font-medium text-muted-foreground">
                                Original Image
                              </h3>
                              <div className="w-full p-3 rounded-lg border border-border">
                                <div className="flex items-start gap-3">
                                  <div className="w-16 h-16 rounded bg-muted animate-pulse flex-shrink-0" />
                                  <div className="flex-1 min-w-0 space-y-2">
                                    <div className="h-3 w-20 bg-muted animate-pulse rounded" />
                                    <div className="h-4 w-full bg-muted animate-pulse rounded" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // DEBUG: Log parent image lookup values
                        console.log("[History Tab] Looking for parent image:", {
                          imageId: image?.id,
                          isEditedImage,
                          toolName: image?.toolName,
                          hasMetadata: !!image?.metadata,
                          parentImageUrl: image?.metadata?.parentImageUrl,
                          original_image_id: image?.metadata?.original_image_id,
                          original_image_url:
                            image?.metadata?.original_image_url,
                          nestedOriginalUrl:
                            image?.metadata?.result?.metadata
                              ?.original_image_url,
                          allMetadataKeys: image?.metadata
                            ? Object.keys(image.metadata)
                            : [],
                          fullMetadata: image?.metadata,
                        });

                        // Helper to normalize URLs for comparison (handles query params, trailing slashes)
                        const normalizeUrl = (url) => {
                          if (!url) return "";
                          try {
                            // Remove query params and trailing slashes for comparison
                            const urlObj = new URL(url);
                            return (
                              urlObj.origin + urlObj.pathname.replace(/\/$/, "")
                            );
                          } catch {
                            // If URL parsing fails, just do basic normalization
                            return url.split("?")[0].replace(/\/$/, "");
                          }
                        };

                        // Helper to find image by URL with flexible matching
                        const findImageByUrl = (targetUrl) => {
                          if (!targetUrl) return null;
                          const normalizedTarget = normalizeUrl(targetUrl);
                          return allImages.find(
                            (img) => normalizeUrl(img.url) === normalizedTarget
                          );
                        };

                        let parentImage = null;

                        // Try to find by parentImageUrl (from tool call arguments)
                        if (image?.metadata?.parentImageUrl) {
                          parentImage = findImageByUrl(
                            image.metadata.parentImageUrl
                          );
                          console.log(
                            "[History Tab] Lookup 1 - parentImageUrl:",
                            {
                              url: image.metadata.parentImageUrl,
                              found: !!parentImage,
                              parentImageId: parentImage?.id,
                            }
                          );
                        }

                        // Try to find by original_image_id
                        if (
                          !parentImage &&
                          image?.metadata?.original_image_id
                        ) {
                          parentImage = allImages.find(
                            (img) => img.id === image.metadata.original_image_id
                          );
                          console.log(
                            "[History Tab] Lookup 2 - original_image_id:",
                            {
                              id: image.metadata.original_image_id,
                              found: !!parentImage,
                              parentImageId: parentImage?.id,
                            }
                          );
                        }

                        // Try to find by original_image_url (from result metadata)
                        if (
                          !parentImage &&
                          image?.metadata?.original_image_url
                        ) {
                          parentImage = findImageByUrl(
                            image.metadata.original_image_url
                          );
                          console.log(
                            "[History Tab] Lookup 3 - original_image_url:",
                            {
                              url: image.metadata.original_image_url,
                              found: !!parentImage,
                              parentImageId: parentImage?.id,
                            }
                          );
                        }

                        // Last resort: check nested result metadata
                        if (
                          !parentImage &&
                          image?.metadata?.result?.metadata?.original_image_url
                        ) {
                          parentImage = findImageByUrl(
                            image.metadata.result.metadata.original_image_url
                          );
                          console.log(
                            "[History Tab] Lookup 4 - nested result metadata:",
                            {
                              url: image.metadata.result.metadata
                                .original_image_url,
                              found: !!parentImage,
                              parentImageId: parentImage?.id,
                            }
                          );
                        }

                        // DEBUG: Final result
                        console.log("[History Tab] Final result:", {
                          parentImageFound: !!parentImage,
                          parentImageId: parentImage?.id,
                          parentImageUrl: parentImage?.url,
                          allImagesCount: allImages.length,
                          allImagesUrls: allImages
                            .slice(0, 5)
                            .map((img) => ({
                              id: img.id,
                              url: img.url?.substring(0, 80),
                            })),
                        });

                        return parentImage ? (
                          <div className="space-y-2">
                            <h3 className="text-sm font-medium text-muted-foreground">
                              Original Image
                            </h3>
                            <button
                              onClick={() => {
                                const shortId = generateShortId(parentImage.id);
                                // Update image state directly (same pattern as handlePreviousImage/handleNextImage)
                                setImage(parentImage);
                                detectAndSaveImageSize(parentImage);
                                // Update URL without navigation (keeps component mounted, prevents render loop)
                                window.history.replaceState(
                                  null,
                                  "",
                                  `/library/image/${shortId}`
                                );
                              }}
                              className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <img
                                  src={getOptimizedImageUrl(
                                    parentImage.url,
                                    ImagePresets.avatar
                                  )}
                                  alt="Original image"
                                  className="w-16 h-16 rounded object-cover flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted-foreground mb-1">
                                    {new Date(
                                      parentImage.createdAt
                                    ).toLocaleDateString()}
                                  </p>
                                  <p className="text-sm line-clamp-2">
                                    {parentImage.prompt}
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>
                        ) : null;
                      })()}

                      {/* Edited Versions Section */}
                      {editedVersions.length > 0 ? (
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-muted-foreground">
                            Edited Versions ({editedVersions.length})
                          </h3>
                          <div className="space-y-2">
                            {editedVersions.map((edit, index) => (
                              <button
                                key={edit.id}
                                onClick={() => {
                                  // Find the target image in allImages
                                  let targetImage = null;
                                  if (edit.imageId) {
                                    targetImage = allImages.find(
                                      (img) => img.id === edit.imageId
                                    );
                                  }
                                  if (!targetImage) {
                                    targetImage = allImages.find(
                                      (img) => img.url === edit.url
                                    );
                                  }

                                  if (targetImage) {
                                    const shortId = generateShortId(
                                      targetImage.id
                                    );
                                    // Update image state directly (same pattern as handlePreviousImage/handleNextImage)
                                    setImage(targetImage);
                                    detectAndSaveImageSize(targetImage);
                                    // Update URL without navigation (keeps component mounted, prevents render loop)
                                    window.history.replaceState(
                                      null,
                                      "",
                                      `/library/image/${shortId}`
                                    );
                                  } else {
                                    // Fallback: open in new tab if image not found in allImages
                                    window.open(edit.url, "_blank");
                                  }
                                }}
                                className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <img
                                    src={getOptimizedImageUrl(
                                      edit.url,
                                      ImagePresets.avatar
                                    )}
                                    alt="Edit preview"
                                    className="w-16 h-16 rounded object-cover flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-muted-foreground mb-1">
                                      Edit #{index + 1} ·{" "}
                                      {new Date(
                                        edit.createdAt
                                      ).toLocaleDateString()}
                                    </p>
                                    <p className="text-sm line-clamp-2">
                                      {edit.prompt}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <Clock className="h-12 w-12 text-muted-foreground/50 mb-4" />
                          <p className="text-sm text-muted-foreground mb-2">
                            No edit history yet
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Edits you make will appear here
                          </p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ImageLightboxPage;
